import { ChangeDetectionStrategy, Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { UsuariosService } from '../../core/usuarios/usuarios.service';
import { UsuarioBusca } from '../../core/usuarios/usuarios.model';

@Component({
  selector: 'app-usuarios',
  standalone: true,
  imports: [FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="page">
      <header class="page__top">
        <div class="page__heading">
          <h1 class="page__title">Usuários — acesso ao portal</h1>
          <p class="page__sub">Conceda acesso de administrador (portal completo). Sem admin, o usuário vê apenas a Conferência.</p>
        </div>
      </header>

      <div class="card">
        <h2 class="card__title">Buscar usuário no Tasy</h2>
        <div class="search-row">
          <input class="input" type="text" placeholder="nome ou login (min. 2 caracteres)"
                 [(ngModel)]="termo" (ngModelChange)="onBusca($event)" />
        </div>
        @if (loadingBusca()) { <p class="state--inline">Buscando…</p> }
        @if (erro(); as e) { <div class="alert alert--erro">{{ e }}</div> }
        @if (resultados().length > 0) {
          <table class="tbl">
            <thead>
              <tr>
                <th>Login</th>
                <th>Nome</th>
                <th class="acoes"></th>
              </tr>
            </thead>
            <tbody>
              @for (u of resultados(); track u.nmUsuario) {
                <tr>
                  <td class="mono">{{ u.nmUsuario }}</td>
                  <td>{{ u.nomePessoa }}</td>
                  <td class="acoes">
                    @if (u.jaEhAdmin) {
                      <span class="status">Já é admin</span>
                    } @else {
                      <button class="btn btn--primary btn--sm" type="button" (click)="conceder(u)">Conceder admin</button>
                    }
                  </td>
                </tr>
              }
            </tbody>
          </table>
        }
      </div>

      <div class="card">
        <h2 class="card__title">Admins atuais</h2>
        @if (admins().length === 0) {
          <div class="state">Nenhum admin cadastrado.</div>
        } @else {
          <table class="tbl">
            <thead>
              <tr>
                <th>Login</th>
                <th>Nome</th>
                <th class="acoes"></th>
              </tr>
            </thead>
            <tbody>
              @for (a of admins(); track a.nmUsuario) {
                <tr>
                  <td class="mono">{{ a.nmUsuario }}</td>
                  <td>{{ a.nomePessoa }}</td>
                  <td class="acoes">
                    <button class="btn btn--sm btn--danger" type="button" (click)="revogar(a)">Revogar</button>
                  </td>
                </tr>
              }
            </tbody>
          </table>
        }
      </div>
    </div>
  `,
  styles: [`
    :host { display: block; }
    .page { padding: 32px 40px; max-width: 900px; margin: 0 auto; font-family: var(--font-sans); color: rgb(var(--color-ink)); display: flex; flex-direction: column; gap: 24px; }
    .page__top { padding-bottom: 20px; border-bottom: 1px solid rgb(var(--color-line)); }
    .page__title { margin: 0; font-size: 24px; font-weight: 600; letter-spacing: -0.02em; }
    .page__sub { margin: 4px 0 0; color: rgb(var(--color-ink-3)); font-size: 13px; }

    .card { background: rgb(var(--color-paper)); border: 1px solid rgb(var(--color-line)); border-radius: var(--radius); padding: 20px 24px; display: flex; flex-direction: column; gap: 14px; }
    .card__title { margin: 0; font-size: 15px; font-weight: 600; }

    .search-row { display: flex; gap: 8px; }
    .input { flex: 1; height: 34px; padding: 0 10px; border: 1px solid rgb(var(--color-line-2)); border-radius: var(--radius-sm); background: rgb(var(--color-bg-soft)); color: rgb(var(--color-ink)); font-size: 13px; font-family: inherit; }
    .input:focus { outline: none; border-color: rgb(var(--color-accent)); }

    .state--inline { font-size: 13px; color: rgb(var(--color-ink-3)); margin: 0; }
    .state { padding: 16px; background: rgb(var(--color-bg-soft)); border-radius: var(--radius-sm); color: rgb(var(--color-ink-3)); text-align: center; font-size: 13px; }
    .alert--erro { background: rgb(var(--color-danger-bg)); border: 1px solid rgb(var(--color-danger)); color: rgb(var(--color-danger)); padding: 10px 14px; border-radius: var(--radius-sm); font-size: 13px; }

    .tbl { width: 100%; border-collapse: collapse; font-size: 13px; }
    .tbl th { text-align: left; padding: 8px 12px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: 0.08em; color: rgb(var(--color-ink-3)); background: rgb(var(--color-bg-soft)); border-bottom: 1px solid rgb(var(--color-line)); }
    .tbl td { padding: 10px 12px; border-bottom: 1px solid rgb(var(--color-line)); vertical-align: middle; }
    .tbl tbody tr:last-child td { border-bottom: none; }
    .tbl .acoes { text-align: right; white-space: nowrap; }
    .mono { font-family: var(--font-mono); font-size: 12px; }

    .btn { height: 30px; padding: 0 12px; border: 1px solid rgb(var(--color-line-2)); border-radius: var(--radius-sm); background: rgb(var(--color-paper)); color: rgb(var(--color-ink)); cursor: pointer; font-size: 12px; font-weight: 500; font-family: inherit; display: inline-flex; align-items: center; transition: background-color 120ms, border-color 120ms; }
    .btn:hover:not(:disabled) { background: rgb(var(--color-hover)); }
    .btn--primary { background: rgb(var(--color-accent)); color: #fff; border-color: rgb(var(--color-accent)); font-weight: 600; }
    .btn--primary:hover:not(:disabled) { background: rgb(var(--color-accent-2)); }
    .btn--danger { color: rgb(var(--color-danger)); border-color: rgb(var(--color-danger)); }
    .btn--danger:hover:not(:disabled) { background: rgb(var(--color-danger-bg)); }
    .btn--sm { height: 26px; padding: 0 10px; font-size: 11px; }

    .status { font-size: 11px; font-weight: 600; padding: 2px 8px; border-radius: 999px; background: rgb(var(--color-accent-bg)); color: rgb(var(--color-accent-2)); }
  `],
})
export class UsuariosComponent implements OnInit {
  private readonly svc = inject(UsuariosService);

  protected termo = '';
  protected readonly resultados = signal<UsuarioBusca[]>([]);
  protected readonly admins = signal<UsuarioBusca[]>([]);
  protected readonly loadingBusca = signal(false);
  protected readonly erro = signal<string | null>(null);

  private buscaTimer: ReturnType<typeof setTimeout> | null = null;

  ngOnInit(): void {
    this.carregarAdmins();
  }

  onBusca(termo: string): void {
    if (this.buscaTimer) clearTimeout(this.buscaTimer);
    if (!termo || termo.trim().length < 2) { this.resultados.set([]); return; }
    this.buscaTimer = setTimeout(() => this.executarBusca(termo.trim()), 350);
  }

  private executarBusca(termo: string): void {
    this.loadingBusca.set(true);
    this.erro.set(null);
    this.svc.buscar(termo).subscribe({
      next: res => { this.resultados.set(res?.data ?? []); this.loadingBusca.set(false); },
      error: () => { this.erro.set('Erro ao buscar usuários.'); this.loadingBusca.set(false); },
    });
  }

  private carregarAdmins(): void {
    this.svc.admins().subscribe({ next: res => this.admins.set(res?.data ?? []) });
  }

  conceder(u: UsuarioBusca): void {
    this.svc.conceder(u.nmUsuario).subscribe({
      next: () => { this.carregarAdmins(); if (this.termo) this.executarBusca(this.termo.trim()); },
      error: () => this.erro.set('Erro ao conceder admin.'),
    });
  }

  revogar(a: UsuarioBusca): void {
    this.svc.revogar(a.nmUsuario).subscribe({
      next: () => { this.carregarAdmins(); if (this.termo) this.executarBusca(this.termo.trim()); },
      error: () => this.erro.set('Erro ao revogar admin.'),
    });
  }
}
